import React from 'react';
import ContactSupportLink from '../components/ContactSupportLink';

const FAQS: { title: string; answer: React.ReactNode }[] = [
    {
        title: 'Why is my pricing page in Draft?',
        answer: (
            <>
                Pricing pages are set to Draft mode until we detect it being loaded for the first time. If you pasted
                the snippet into your HTML trying visiting the page in your browser (you should see your pricing page
                appear).
            </>
        ),
    },
    {
        title: 'Is PriceWell compliant with GDPR regulations?',
        answer: (
            <>
                Yes. PriceWell does not store any personally identifiable information of your customers. That
                information is all held in your Stripe account. We only store the identifier to your Stripe account (and
                use that to trigger payment flows for your customers. This means you do not need to remove any personal
                user data from PriceWell, but you may need to from your Stripe account (see the{' '}
                <a
                    href="https://stripe.com/en-de/guides/general-data-protection-regulation"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link"
                >
                    Stripe GDPR Documentation
                </a>
                ).
            </>
        ),
    },
    {
        title: 'Can I link multiple Stripe accounts to one PriceWell account?',
        answer: (
            <>
                This feature is coming soon. Please <ContactSupportLink /> to bug us about it.
            </>
        ),
    },
    {
        title: 'Can I use my Stripe account in Test mode?',
        answer: (
            <>
                Yes. You need to connect your live Stripe account in order to set up your PriceWell account. Once your
                account is connected you can switch an individual Pricing Page between Test and Live mode. In test mode
                you can use test credit cards (see{' '}
                <a
                    href="</a>https://stripe.com/docs/testing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link"
                >
                    https://stripe.com/docs/testing
                </a>
                ) to test the full process. If a page is in test mode, a banner will be displayed when your pricing page
                is loaded (so you don't forget to switch it to live mode before you launch it to customers).
            </>
        ),
    },
];

function partition<T>(values: T[], partitionSize: number) {
    const zeroBasePartition = partitionSize - 1;
    return values.reduce<T[][]>(
        (prev, _curr, idx) =>
            idx % zeroBasePartition === 0
                ? [...prev, values.slice(idx, Math.min(idx + zeroBasePartition, values.length))]
                : prev,
        []
    );
}
export default function HelpPage() {
    const groupedFAQS = partition(FAQS, 4);
    return (
        <section className="w-full text-gray-700">
            <div className="container px-5 py-24 mx-auto">
                <div className="text-center mb-20">
                    <h1 className="sm:text-3xl text-2xl font-medium text-center title-font text-gray-900 mb-4">
                        Frequently Asked Questions
                    </h1>
                    <p className="text-base leading-relaxed xl:w-2/4 lg:w-3/4 mx-auto">
                        Take a look at the most common questions we're asked about PriceWell to see if we've already
                        answered your question.
                    </p>
                </div>
                <div className="flex flex-wrap lg:w-4/5 sm:mx-auto sm:mb-2 -mx-2">
                    {groupedFAQS.map((group, idx) => (
                        <div key={idx} className="w-full lg:w-1/2 px-4 py-2">
                            {group.map((item) => (
                                <details key={item.title} className="mb-4">
                                    <summary className="font-semibold  bg-gray-200 rounded-md py-2 px-4">
                                        {item.title}
                                    </summary>

                                    <p>{item.answer}</p>
                                </details>
                            ))}
                        </div>
                    ))}
                </div>
                <div className="text-center my-20">
                    <h1 className="sm:text-3xl text-2xl font-medium text-center title-font text-gray-900 mb-4">
                        Can't find what you are looking for?
                    </h1>
                    <p className="text-base leading-relaxed xl:w-2/4 lg:w-3/4 mx-auto">
                        <ContactSupportLink titleCase />
                    </p>
                </div>
            </div>
        </section>
    );
}
